import React from 'react';
import {
  documentToReactComponents,
  Options as DocumentToReactComponentOptions,
} from '@contentful/rich-text-react-renderer';
import { ContentfulEntry } from './ContentfulEntry';
import {
  ArticleRichTextLayoutBlock,
  ArticleRichTextLayoutBlockStyleProps,
} from './ArticleRichTextLayoutBlock/ArticleRichTextLayoutBlock';
import { Block, BLOCKS, Document, Inline } from '@contentful/rich-text-types';
import { Quote } from '../Quote/Quote';
import { renderListItem } from '@jbhi-fi/components/src/RichText/renderListItem';
import { ContentBlock, ContentBlockStyleProps } from '@jbhi-fi/components/src/ContentBlock';

export const renderEmbeddedEntry = (
  node: Block | Inline,
  contentBlockProps?: ContentBlockStyleProps
): React.ReactNode => {
  return contentBlockProps ? (
    <ContentBlock {...contentBlockProps}>
      <ArticleRichTextElement entry={new ContentfulEntry(node)} />
    </ContentBlock>
  ) : (
    <ArticleRichTextElement entry={new ContentfulEntry(node)} />
  );
};

export const renderText = (
  node: Block | Inline,
  layoutBlockProps: ArticleRichTextLayoutBlockStyleProps = { paddingTop: 4 },
  contentBlockProps?: ContentBlockStyleProps
): React.ReactNode => {
  const Wrapper = contentBlockProps ? ContentBlock : React.Fragment;
  return (
    <Wrapper {...(contentBlockProps ?? {})}>
      <ArticleRichTextLayoutBlock {...layoutBlockProps}>
        {documentToReactComponents(node as Document, {
          renderNode: {
            [BLOCKS.EMBEDDED_ASSET]: node => renderEmbeddedEntry(node, contentBlockProps),
            [BLOCKS.EMBEDDED_ENTRY]: node => renderEmbeddedEntry(node, contentBlockProps),
            [BLOCKS.QUOTE]: (_, children) => <Quote>{children}</Quote>,
            [BLOCKS.LIST_ITEM]: renderListItem,
          },
        })}
      </ArticleRichTextLayoutBlock>
    </Wrapper>
  );
};

export const renderOptions = (contentBlockProps?: ContentBlockStyleProps): DocumentToReactComponentOptions => ({
  renderNode: {
    [BLOCKS.EMBEDDED_ASSET]: node => renderEmbeddedEntry(node, contentBlockProps),
    [BLOCKS.EMBEDDED_ENTRY]: node => renderEmbeddedEntry(node, contentBlockProps),
    [BLOCKS.PARAGRAPH]: node => renderText(node, undefined, contentBlockProps),
    [BLOCKS.UL_LIST]: node => renderText(node, { paddingTop: 3.5 }, contentBlockProps),
    [BLOCKS.OL_LIST]: node => renderText(node, { paddingTop: 3.5 }, contentBlockProps),
    [BLOCKS.HR]: node => renderText(node, { paddingTop: { mobile: 7.5, desktop: 10 } }, contentBlockProps),
    [BLOCKS.HEADING_1]: node => renderText(node, { paddingTop: 10 }, contentBlockProps),
    [BLOCKS.HEADING_2]: node => renderText(node, { paddingTop: 10 }, contentBlockProps),
    [BLOCKS.HEADING_3]: node => renderText(node, { paddingTop: 10 }, contentBlockProps),
    [BLOCKS.HEADING_4]: node => renderText(node, undefined, contentBlockProps),
    [BLOCKS.HEADING_5]: node => renderText(node, undefined, contentBlockProps),
    [BLOCKS.HEADING_6]: node => renderText(node, undefined, contentBlockProps),
    [BLOCKS.QUOTE]: node => renderText(node, { paddingTop: 8.25, paddingBottom: 8.25 }, contentBlockProps),
  },
});

export type ArticleRichTextProps = {
  document: Document;
  renderOptions?: DocumentToReactComponentOptions;
  contentBlockProps?: ContentBlockStyleProps;
};

export const ArticleRichText: React.FC<ArticleRichTextProps> = ({
  document,
  renderOptions: renderOptionsProp = {},
  contentBlockProps,
}) => {
  const options: DocumentToReactComponentOptions = {
    ...renderOptions(contentBlockProps),
    ...renderOptionsProp,
    renderNode: {
      ...renderOptions(contentBlockProps).renderNode,
      ...(renderOptionsProp?.renderNode ?? {}),
    },
  };

  return <>{documentToReactComponents(document, options)}</>;
};

export type ArticleRichTextElementProps = {
  entry: ContentfulEntry;
};

export const ArticleRichTextElement: React.FC<ArticleRichTextElementProps> = ({ entry }) => {
  if (!entry?.fields) return null;
  return (
    <ArticleRichTextLayoutBlock {...entry.layoutBlockProps}>
      <entry.OutputComponent {...entry.componentProps} />
    </ArticleRichTextLayoutBlock>
  );
};
